import { createStyles, Group } from '@mantine/core';
import { useRichTextEditorContext } from '@mantine/tiptap';
import { IconTableShare } from '@tabler/icons-react';
import { PluginKey } from '@tiptap/pm/state';
import { BubbleMenu } from '@tiptap/react';
import Button from 'components/Button';
import P4Medium from 'components/typography/P4Medium';
import {
  canMergeTableWithAbove,
  canMergeTableWithBelow,
} from 'pages/revisions-module/template-editor/editors/template/extensions/table-merge';
import { useMemo } from 'react';
import { Props } from 'tippy.js';

const tableMergeAboveBubbleMenuKey = new PluginKey('tableMergeAboveBubbleMenu');
const tableMergeBelowBubbleMenuKey = new PluginKey('tableMergeBelowBubbleMenu');

const useStyles = createStyles((theme) => ({
  button: {
    boxShadow: '0px 4px 12px 0px rgba(197, 198, 211, 0.40) !important',
    border: `1px solid ${theme.colors.red[3]}`,
  },
}));

/**
 * Bubble menus used to merge the focused table with a direct neighboring table.
 */
export default function TableMergeBubbleMenu() {
  const { classes } = useStyles();
  const { editor } = useRichTextEditorContext();

  const topTippyOptions: Partial<Props> = useMemo(
    () => ({
      duration: 100,
      placement: 'top-start',
      zIndex: 3,
      getReferenceClientRect: () => getTableCornerRect(editor, 'top'),
      offset: [0, 6],
    }),
    [editor]
  );

  const bottomTippyOptions: Partial<Props> = useMemo(
    () => ({
      duration: 100,
      placement: 'bottom-start',
      zIndex: 3,
      getReferenceClientRect: () => getTableCornerRect(editor, 'bottom'),
      offset: [0, 6],
    }),
    [editor]
  );

  if (!editor) {
    return null;
  }

  return (
    <>
      <BubbleMenu
        editor={editor}
        pluginKey={tableMergeAboveBubbleMenuKey}
        tippyOptions={topTippyOptions}
        shouldShow={({ editor: currentEditor }) => canMergeTableWithAbove(currentEditor.state)}
      >
        <Button
          size="sm"
          variant="secondary"
          onClick={() => editor.chain().focus().mergeTableWithAbove().run()}
          className={classes.button}
        >
          <Group spacing={2}>
            <IconTableShare size={12} />
            <P4Medium fw={700} pr={2}>
              Spojiť s tabuľkou vyššie
            </P4Medium>
          </Group>
        </Button>
      </BubbleMenu>
      <BubbleMenu
        editor={editor}
        pluginKey={tableMergeBelowBubbleMenuKey}
        tippyOptions={bottomTippyOptions}
        shouldShow={({ editor: currentEditor }) => canMergeTableWithBelow(currentEditor.state)}
      >
        <Button
          size="sm"
          variant="secondary"
          onClick={() => editor.chain().focus().mergeTableWithBelow().run()}
          className={classes.button}
        >
          <Group spacing={2}>
            <IconTableShare size={12} />
            <P4Medium fw={700} pr={2}>
              Spojiť s tabuľkou nižšie
            </P4Medium>
          </Group>
        </Button>
      </BubbleMenu>
    </>
  );
}

/**
 * Returns the requested corner rect of the focused table.
 */
function getTableCornerRect(
  editor: NonNullable<ReturnType<typeof useRichTextEditorContext>['editor']>,
  edge: 'top' | 'bottom'
) {
  const table = getFocusedTableElement(editor);

  if (!table) {
    return editor.view.dom.getBoundingClientRect();
  }

  const rect = table.getBoundingClientRect();
  const x = rect.right + 8;
  const y = edge === 'top' ? rect.top + 8 : rect.bottom - 8;

  return {
    x,
    y,
    top: y,
    bottom: y,
    left: x,
    right: x,
    width: 0,
    height: 0,
    toJSON() {
      return this;
    },
  };
}

/**
 * Returns the currently focused table element from the DOM selection anchor.
 */
function getFocusedTableElement(editor: NonNullable<ReturnType<typeof useRichTextEditorContext>['editor']>) {
  let node: Node | null = editor.view.domAtPos(editor.state.selection.anchor).node;

  while (node) {
    if (node instanceof HTMLTableElement) {
      return node;
    }

    if (node instanceof HTMLElement) {
      const table = node.closest('table');

      if (table instanceof HTMLTableElement) {
        return table;
      }
    }

    node = node.parentNode;
  }

  return null;
}
